import { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency, STAGE_ORDER } from '../../utils/formatters';

const COLORS = [
  '#1e3a5f', '#c9952a', '#2d7a4f', '#c0392b',
  '#8e44ad', '#16a085', '#d35400', '#2980b9',
  '#27ae60', '#e74c3c',
];

function advancedBeyondPIF(deal) {
  const idx = STAGE_ORDER.indexOf(deal.current_stage);
  return idx > 0 || (deal.status === 'active' && !STAGE_ORDER.slice(0,1).includes(deal.current_stage));
}

function groupBy(deals, key) {
  const map = {};
  deals.forEach(d => {
    const k = d[key] || 'Unknown';
    if (!map[k]) map[k] = [];
    map[k].push(d);
  });
  return map;
}

function calcStats(deals, fund) {
  const currency = fund?.base_currency || '';
  const grouped = groupBy(deals, 'sector');
  return Object.entries(grouped).map(([sector, sDeals]) => {
    const totalAmount = sDeals.reduce((s, d) => s + (d.requested_amount || 0), 0);
    const scores = sDeals.filter(d => d.pif_score != null).map(d => d.pif_score);
    const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2) : '—';
    const advanced = sDeals.filter(advancedBeyondPIF).length;
    const womenLed = sDeals.filter(d => d.is_women_led === true).length;
    return {
      sector,
      count: sDeals.length,
      totalAmount,
      currency,
      avgScore,
      advRate: sDeals.length > 0 ? Math.round((advanced / sDeals.length) * 100) : 0,
      womenLed,
      womenLedPct: sDeals.length > 0 ? Math.round((womenLed / sDeals.length) * 100) : 0,
    };
  }).sort((a, b) => b.count - a.count);
}

export default function SectorAnalysisView({ deals, fund }) {
  const [womenOnly, setWomenOnly] = useState(false);
  const [sortCol, setSortCol] = useState('count');
  const [sortDir, setSortDir] = useState('desc');

  const filtered = womenOnly ? deals.filter(d => d.is_women_led === true) : deals;
  const allStats = calcStats(filtered, fund);
  const pieData = allStats.map(r => ({ name: r.sector, value: r.count }));
  const rows = allStats.sort((a, b) => {
    const av = a[sortCol]; const bv = b[sortCol];
    return sortDir === 'desc' ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
  });

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  }
  const th = (col, label) => (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col)}>
      {label} {sortCol === col ? (sortDir === 'desc' ? '↓' : '↑') : ''}
    </th>
  );

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={womenOnly} onChange={e => setWomenOnly(e.target.checked)} />
          Show only women-led companies
        </label>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Deal Count by Sector</h2></div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value} deals`, 'Count']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h2>Sector Breakdown</h2></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {th('sector', 'Sector')}
                {th('count', 'Deals')}
                {th('totalAmount', 'Total Requested')}
                {th('avgScore', 'Avg PIF Score')}
                {th('advRate', 'Advancement Rate')}
                {th('womenLed', 'Women-Led')}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.sector}>
                  <td style={{ fontWeight: 500 }}>{r.sector}</td>
                  <td>{r.count}</td>
                  <td style={{ fontSize: 12 }}>{formatCurrency(r.totalAmount, r.currency)}</td>
                  <td>{r.avgScore}</td>
                  <td>{r.advRate}%</td>
                  <td>{r.womenLed} <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>({r.womenLedPct}%)</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
